import { useEffect } from 'react';

// Extend the Window interface to include grecaptcha
declare global {
	interface Window {
		grecaptcha: {
			ready: (callback: () => void) => void;
			execute: (siteKey: string, options?: { action: string }) => Promise<string>;
		};
	}
}

export const useRecaptcha = (siteKey: string) => {
	useEffect(() => {
		if (!siteKey) {
			return;
		}

		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);

		return () => {
			document.head.removeChild(script); // Cleanup on unmount
		};
	}, [siteKey]);

	const executeRecaptcha = async (action: string): Promise<string> => {
		const url = new URL(window.location.href);

		if (url.searchParams.has('recaptcha-token')) {
			return url.searchParams.get('recaptcha-token') || '';
		}

		if (!window.grecaptcha) {
			console.warn('The Google reCAPTCHA script could not be executed, so no token was generated.');
			return '';
		}

		return new Promise((resolve, reject) => {
			window.grecaptcha.ready(() => {
				window.grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
			});
		});
	};

	const generateRecaptchaToken = async (action: string): Promise<string> => {
		if (!siteKey) {
			console.warn('No reCAPTCHA site key provided.');
			return '';
		}

		try {
			return await executeRecaptcha(action);
		} catch (error) {
			console.error('Error getting reCAPTCHA token:', error);
			return '';
		}
	};

	return { generateRecaptchaToken };
};
